import * as PoziUtils from 'api/utils'

export interface KeywordOption {
  multiple: boolean | 'array' | 'object'
  type: 'boolean' | 'number' | 'params' | 'string'
}

// Freeze the object to allow TS to properly infer types from this object
const allowedOptions = Object.freeze({
  dataUrl: {
    multiple: false,
    type: 'string'
  },
  description: {
    multiple: false,
    type: 'string'
  },
  downloadable: {
    multiple: false,
    type: 'boolean'
  },
  enabled: {
    multiple: false,
    type: 'boolean'
  },
  group: {
    multiple: false,
    type: 'string'
  },
  InfoPanelCollapse: {
    multiple: false,
    type: 'boolean'
  },
  initialRevealFields: {
    multiple: false,
    type: 'number'
  },
  linkedLayers: {
    multiple: 'array',
    type: 'string'
  },
  linkOuts: {
    multiple: 'array',
    type: 'string'
  },
  loader: {
    multiple: false,
    type: 'string'
  },
  parameter: {
    multiple: 'object',
    type: 'params'
  },
  parent: {
    // multiple: 'array', // maybe in the future
    multiple: false,
    type: 'string'
  },
  profiles: {
    multiple: 'array',
    type: 'string'
  },
  promoteDetails: {
    multiple: false,
    type: 'boolean'
  },
  searchFields: {
    multiple: 'array',
    type: 'string'
  },
  showInLayerControl: {
    multiple: false,
    type: 'boolean'
  },
  showLegend: {
    multiple: false,
    type: 'boolean'
  },
  styleUrl: {
    multiple: false,
    type: 'string'
  },
  type: {
    multiple: false,
    type: 'string'
  },
  visible: {
    multiple: false,
    type: 'boolean'
  }
})

// Keyword options that have multiple values need to be separated by this character
const PARAM_VALUES_SEPARATOR = ';'

// Derive valid options from the keys of the object, i.e.
// 'parameter' | 'parent' | 'showInLayerControl', etc
export type ValidOptionsKeys = keyof typeof allowedOptions

export type ValidKeywordOptions = Partial<Record<ValidOptionsKeys, any>>

// export type KeywordOption = typeof allowedOptions

export type KeywordOptions = Record<ValidOptionsKeys, KeywordOption> | Record<string, never>

export default class KeywordsParser {
  static formatValue = (value: string, type: KeywordOption['type']) => {
    switch (type) {
      case 'boolean': {
        const valueStr = String(value).toLowerCase().trim()
        return valueStr !== '' && valueStr !== '0' && valueStr !== 'false'
      }
      case 'number':
        return Number(value)
      case 'params': {
        // Split params string on first occurrence of '='. Example:
        // 'EXP_FILTER=assetID = [Asset_ID]' => ['EXP_FILTER', 'assetID = [Asset_ID]']
        // 'FILTER=<Filter><Intersects><PropertyName>geom</PropertyName>[$gml]</Intersects></Filter>'
        //    =>
        //    ['FILTER', '<Filter><Intersects><PropertyName>geom</PropertyName>[$gml]</Intersects></Filter>']
        // and
        // 'RANDOM' => ['RANDOM', '']
        const paramsMatch = value.match(/^(\w+)\s*=?\s*(.*)$/)

        let paramKey, paramValue
        if (paramsMatch) {
          [, paramKey, paramValue] = paramsMatch
        } else {
          paramKey = ''
          paramValue = ''
        }

        // Force param key to be upper-case
        paramKey = paramKey.toUpperCase()

        // Handle params without a value: we'll give them the same value as their name
        if (typeof paramValue === 'undefined' || paramValue === '') {
          paramValue = paramKey
        }

        // For param values that need to have a comma in them, like EXP_FILTER values
        // We cater for case and whitespace.
        // Example: EXP_FILTER=intersects(@geometry [$comma] buffer(geom_from_wkt('[$wkt]') [$comma] -0.00001 ))
        paramValue = paramValue.replace(/\[\s*\$comma\s*\]/ig, ',')

        return [paramKey, paramValue]
      }
      case 'string':
      default:
        return String(value)
    }
  }

  // Return an options object with parsed values from special keywords, i.e. the ones
  // that have '=' in the string
  static getOptions (keywords: string[]): ValidKeywordOptions {
    // TODO: define a type for the options object
    const options: ValidKeywordOptions = {}

    for (const keyword of keywords) {
      // console.debug('KeywordsParser - getOptions: keyword', keyword)

      // The keyword needs to be something like: 'foobar', 'foo_bar'
      const match = keyword.match(/([_\w]+)=(.+)/)

      // Only process entries that match the following pattern: <key>=<value>
      if (!match) {
        // If the string contains '=', maybe the field is misconfigured.
        if (keyword.includes('=')) {
          console.warn('KeywordsParser - getOptions: Failed to match. Misconfiguration?', `'${keyword}'`)
        } else {
          // console.debug('KeywordsParser - getOptions: Normal keyword. Ignoring.', `'${keyword}'`)
        }
        continue
      }

      // Extract variables from the match object
      const [, key, value] = match

      // console.debug('KeywordsParser - getOptions: keyword match', key, value)

      // Do a case-insensitive lookup for the key, to allow keys
      // like 'PromoteDETAILS' to match 'promoteDetails'
      const matchedKey = PoziUtils.findPropName(allowedOptions, key)

      // Only allow options from whitelist to prevent unexpected outcomes
      if (!matchedKey) {
        console.debug('KeywordsParser - getOptions: keyword option not in whitelist:', key, value)

        continue
      }

      // Cast to a type to make typescript happy
      const validKey = matchedKey as ValidOptionsKeys

      const keywordOptions = allowedOptions[validKey]

      // 3 modes: 'array', 'object' or false
      const multiple = keywordOptions?.multiple ?? false

      // Can be things like 'boolean', 'number', 'string' or special type 'params'
      const valueType = keywordOptions.type as KeywordOption['type']

      // Turn value in array, just in case it has multiple values (separated by ';')
      const valueArray = value.split(PARAM_VALUES_SEPARATOR).map(v => v.trim())
      const validValueArray = valueArray.map(value => KeywordsParser.formatValue(value, valueType))

      // console.debug('KeywordsParser - value array', valueArray, validValueArray)

      if (multiple) {
        // Convert array into object if the options dictate this
        if (multiple === 'object') {
          // @ts-expect-error
          const validValueObject = Object.fromEntries(validValueArray)
          // console.debug('KeywordsParser - getOptions: valid value object', validKey, validValueObject)

          options[validKey] = validValueObject
        } else {
          options[validKey] = validValueArray
        }
      } else {
        // When only take the first value if multiple is set to false
        options[validKey] = validValueArray[0]
      }
    }

    // console.debug('KeywordsParser - getOptions: options', { ...options })

    return options
  }

  // Only return keywords that do not get processed as options, i.e. do not have '=' in the string
  static getSanitisedKeywords (keywords: string[]) {
    const sanitisedKeywords = keywords.filter(keyword => keyword && !keyword.includes('='))

    // console.debug('KeywordsParser - getSantisedKeywords', sanitisedKeywords)

    return sanitisedKeywords
  }
}
